import { useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import type { FocusEvent } from 'react';
import { useCallback, useMemo, useState } from 'react';
import type { UseFormSetValue } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';

import { getLineItemsApi } from 'api/repairOrder';
import { useTickets } from 'contexts/TicketsContext';
import { useUserProfile } from 'contexts/UserProfileContext';
import { RequestQueryKeysE } from 'declarations/enums/react-query';
import { TicketsRefinementKeysE } from 'declarations/enums/tickets';
import type { ApiErrorI } from 'declarations/interfaces/errors';
import type { LineItemI } from 'declarations/interfaces/lineItems';
import type { TicketRequestCategoryI } from 'declarations/interfaces/ticketRequestCategories';
import type { TicketRequestSubcategoryI } from 'declarations/interfaces/ticketRequestSubcategories';
import type { NoopT } from 'declarations/types/common';
import type { SelectOptionT } from 'declarations/types/select';
import { getErrorMessage, getValidationErrorMessage } from 'helpers/apiError';
import { useFetchClaimStatuses } from 'hooks/api/useClaimStatuses';
import { useFetchTicketPriorities } from 'hooks/api/useTicketPriorities';
import { useFetchTicketRequestCategories } from 'hooks/api/useTicketRequestCategories';
import { useFetchTicketRoExists } from 'hooks/api/useTicketRoNumber';
import { useCreateTicket } from 'hooks/api/useTickets';
import { useFetchTicketRequestSubcategoriesByCategoryId } from 'hooks/api/useTicketSubcategories';
import { updateSnackbarNotification } from 'reduxStore/features/notifications/notificationsSlice';
import { useAppDispatch } from 'reduxStore/hooks';

import type { NewTicketFormValuesT } from '../declarations';

type UseOpenNewTicketPopupT = {
  requestCategoriesOptions: SelectOptionT[];
  isLoadingRequestCategoriesOptions: boolean;
  ticketRequestSubcategoriesOptions: SelectOptionT[];
  isLoadingTicketRequestSubcategoriesOptions: boolean;
  claimStatusesOptions: SelectOptionT[];
  isLoadingClaimStatusesOptions: boolean;
  ticketPrioritiesOptions: SelectOptionT[];
  isLoadingTicketPrioritiesOptions: boolean;
  isLoadingRoofTopOptions: boolean;
  roofTopIdsOptions: SelectOptionT[];
  isPopupOpened: boolean;
  handleSubmitForm: (data: NewTicketFormValuesT) => void;
  handleAmountBlur: (
    setValue: UseFormSetValue<NewTicketFormValuesT>
  ) => (e: FocusEvent<HTMLInputElement>) => void;
  isCreatingNewTicket: boolean;
  handleCloseNewTicketPopup: NoopT;
  handleRoofTopChange: (roofTopId: string) => void;
  handleCategoryChange: (categoryId: number) => void;
  ticketRequestCategories: TicketRequestCategoryI[] | undefined;
  ticketRequestSubcategories: TicketRequestSubcategoryI[] | undefined;
  advisorNumber: string | undefined;
  checkIfRoExists: (
    roofTopId: string,
    roNumber: string,
    setValue: UseFormSetValue<NewTicketFormValuesT>
  ) => void;
  handleLineNumberChange: (lineNumber: string) => void;
  handleROChange: (roNumber: string) => void;
  lineItemsObj: LineItemI[];
  lineNumbers: SelectOptionT[];
  recentRoData: { roofTopId: string | null; roNumber: string | null };
  setAdvisorNumber: React.Dispatch<React.SetStateAction<string | undefined>>;
  setLineItemsObj: React.Dispatch<React.SetStateAction<LineItemI[]>>;
  setLineNumbers: React.Dispatch<React.SetStateAction<SelectOptionT[]>>;
  setRecentRoData: React.Dispatch<
    React.SetStateAction<{ roofTopId: string | null; roNumber: string | null }>
  >;
  setShowLineNumberDropdown: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedLineNumber: React.Dispatch<React.SetStateAction<string | undefined>>;
  setSelectedRoofTopId: React.Dispatch<React.SetStateAction<string | undefined>>;
  showLineNumberDropdown: boolean;
  handleAdvisorNumberChange: (
    advisorNumber: string,
    setValue: UseFormSetValue<NewTicketFormValuesT>
  ) => void;
};

const useOpenNewTicketPopup = (): UseOpenNewTicketPopupT => {
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const { setNewTicketPopupVisibility, isNewTicketPopupOpened } = useTickets();
  const [selectedRoofTopId, setSelectedRoofTopId] = useState<string | undefined>(undefined);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | undefined>(undefined);
  const { userProfile, isLoadingUserProfile } = useUserProfile();
  const queryClient = useQueryClient();
  const [, setSelectedRoNumber] = useState<string | null>(null);
  const [, setSelectedLineNumber] = useState<string | undefined>(undefined);
  const [showLineNumberDropdown, setShowLineNumberDropdown] = useState(false);
  const [advisorNumber, setAdvisorNumber] = useState<string | undefined>(undefined);
  const [lineNumbers, setLineNumbers] = useState<SelectOptionT[]>([]);
  const [lineItemsObj, setLineItemsObj] = useState<LineItemI[]>([]);
  const [recentRoData, setRecentRoData] = useState<{
    roofTopId: string | null;
    roNumber: string | null;
  }>({
    roofTopId: null,
    roNumber: null,
  });

  const roofTopFromSearchParams = searchParams.get(TicketsRefinementKeysE.RoofTopId) ?? undefined;
  const effectiveRoofTopId = selectedRoofTopId ?? roofTopFromSearchParams;

  const ticketRequestCategoriesResult = useFetchTicketRequestCategories(
    isNewTicketPopupOpened,
    effectiveRoofTopId
  );
  const ticketRequestSubcategoriesResult = useFetchTicketRequestSubcategoriesByCategoryId({
    isEnabled: isNewTicketPopupOpened && !!effectiveRoofTopId && !!selectedCategoryId,
    categoryId: selectedCategoryId,
    roofTopId: effectiveRoofTopId,
  });
  const roofTopId = searchParams.get(TicketsRefinementKeysE.RoofTopId) || selectedRoofTopId || '';
  const claimStatusesRequestParams = {
    isEnabled: isNewTicketPopupOpened,
    roofTopId: roofTopId,
  };

  const claimStatusesResult = useFetchClaimStatuses(claimStatusesRequestParams);
  const ticketPrioritiesResult = useFetchTicketPriorities(isNewTicketPopupOpened);
  const createTicketMutation = useCreateTicket({
    onSuccess: () => {
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTickets]);
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTicketsCountByRoofTop]);
    },
  });
  const roQuery = useFetchTicketRoExists(
    recentRoData.roofTopId as string,
    recentRoData.roNumber as string
  );

  const requestCategoriesOptions = useMemo(
    () =>
      ticketRequestCategoriesResult.data
        ? ticketRequestCategoriesResult.data
            .filter(({ categoryName }) => categoryName.toLowerCase() !== 'schedule')
            .map(({ id, categoryName }) => ({
              label: categoryName,
              value: id,
            }))
        : [],
    [ticketRequestCategoriesResult.data]
  );
  const ticketRequestSubcategoriesOptions = useMemo(
    () =>
      ticketRequestSubcategoriesResult.data
        ? ticketRequestSubcategoriesResult.data?.map(({ id, name }) => ({
            label: name,
            value: id,
          }))
        : [],
    [ticketRequestSubcategoriesResult.data]
  );
  const claimStatusesOptions = useMemo(
    () =>
      claimStatusesResult.data
        ? claimStatusesResult.data?.map(({ id, name }) => ({
            label: name,
            value: id,
          }))
        : [],
    [claimStatusesResult.data]
  );
  const ticketPrioritiesOptions = useMemo(
    () =>
      ticketPrioritiesResult.data
        ? ticketPrioritiesResult.data?.map(({ id, name }) => ({
            label: name,
            value: id,
          }))
        : [],
    [ticketPrioritiesResult.data]
  );
  const roofTopIdsOptions = useMemo(
    () =>
      userProfile
        ? userProfile?.mappedRoofTops
            .map(({ id, name }) => ({ label: name, value: id }))
            .sort((a, b) => a.label.localeCompare(b.label))
        : [],
    [userProfile]
  );

  const resetTicketMutationState = useCallback((): void => {
    if (createTicketMutation.isSuccess || createTicketMutation.isError) {
      createTicketMutation.reset();
    }
  }, [createTicketMutation]);

  const handleCloseNewTicketPopup = useCallback((): void => {
    setNewTicketPopupVisibility(false);
    setSelectedCategoryId(undefined);
    resetTicketMutationState();
  }, [resetTicketMutationState, setNewTicketPopupVisibility]);

  const setAmountValue = (value: string, setValue: UseFormSetValue<NewTicketFormValuesT>): void => {
    setValue('amount', value, { shouldValidate: true });
  };

  const handleAmountBlur = useCallback(
    (setValue: UseFormSetValue<NewTicketFormValuesT>) =>
      (e: FocusEvent<HTMLInputElement>): void => {
        const { value } = e.target;
        const [numbersBeforeDot, numbersAfterDot] = value.split('.');
        const hasOneDecimal = !!numbersAfterDot && numbersAfterDot.length === 1;
        const hasTwoDecimal = !!numbersAfterDot && numbersAfterDot.length >= 2;

        if (!value) {
          setAmountValue('0.00', setValue);
        } else if (value && hasOneDecimal) {
          setAmountValue(`${numbersBeforeDot}.${numbersAfterDot}0`, setValue);
        } else if (value && !hasTwoDecimal) {
          setAmountValue(`${numbersBeforeDot}.00`, setValue);
        }
      },
    []
  );

  const handleSubmitForm = useCallback(
    (data: NewTicketFormValuesT) => {
      const onMutationSuccess = (): void => {
        dispatch(
          updateSnackbarNotification({
            snackbarMessage: 'Ticket successfully created',
            snackBarSeverity: 'success',
          })
        );
        handleCloseNewTicketPopup();
      };
      const onMutationError = (error: unknown): void => {
        const errorMsg =
          getErrorMessage(error as ApiErrorI) || getValidationErrorMessage(error as ApiErrorI);
        dispatch(
          updateSnackbarNotification({
            snackbarMessage: `Something went wrong. ${errorMsg || ''}`,
            snackBarSeverity: 'error',
          })
        );
        handleCloseNewTicketPopup();
      };

      const parsedData = {
        requestCategoryId: data.requestCategoryId,
        requestSubCategoryId: data.requestSubcategoryId || null,
        requestSubCategoryName: data.requestSubCategoryName || null,
        roDate: data.roDate ? format(data.roDate, 'yyyy-MM-dd') : '1970-01-01',
        amount: Number(data.amount),
        text: data.text,
        roofTopId: data.roofTopId,
        roNumber: data.roNumber,
        lineNumber: data.lineNumber,
        techNumber: data.techNumber,
        advisorNumber: data.advisorNumber,
        vin: data.vin ?? undefined,
        claimStatusId: data.claimStatusId,
        ticketPriorityId: data.ticketPriorityId,
        claimSubmittedDate: data.claimSubmittedDate
          ? format(data.claimSubmittedDate, 'yyyy-MM-dd')
          : null,
        subCategoryId: data.subCategoryId || null,
      };
      createTicketMutation.mutate(parsedData, {
        onSuccess: onMutationSuccess,
        onError: onMutationError,
      });
    },
    [createTicketMutation, dispatch, handleCloseNewTicketPopup]
  );

  const handleRoofTopChange = useCallback((roofTopId: string): void => {
    setSelectedRoofTopId(roofTopId);
    setSelectedCategoryId(undefined);
    setRecentRoData(prevRoData => ({
      ...prevRoData,
      roofTopId: roofTopId,
    }));
  }, []);

  const handleROChange = (roNumber: string | null): void => {
    setSelectedRoNumber(roNumber);
    setRecentRoData(prevRoData => ({
      ...prevRoData,
      roNumber: roNumber,
    }));
  };

  const handleAdvisorNumberChange = (
    advisorNumber: string,
    setValue: UseFormSetValue<NewTicketFormValuesT>
  ): void => {
    setAdvisorNumber(advisorNumber);
    setValue('advisorNumber', advisorNumber);
  };

  const handleLineNumberChange = (lineNumber: string): void => {
    setSelectedLineNumber(recentRoData.roNumber ? lineNumber : '');
  };

  const handleCategoryChange = useCallback((categoryId: number): void => {
    setSelectedCategoryId(categoryId);
  }, []);

  const setAdvisorLineNumber = async (
    roofTopId: string,
    roNumber: string,
    setValue: UseFormSetValue<NewTicketFormValuesT>
  ): Promise<void> => {
    setRecentRoData({ roofTopId, roNumber });
    const roResult = roQuery.data;
    setValue('advisorNumber', roResult?.serviceAdvisorNumber ?? advisorNumber ?? '');
    setAdvisorNumber(roResult?.serviceAdvisorNumber ?? advisorNumber);
    if (roQuery.isError || (roNumber && roQuery.data === undefined)) {
      setShowLineNumberDropdown(false);
      setSelectedLineNumber(undefined);
      setValue('lineNumber', '');
      setLineItemsObj([]);
    } else {
      const lineItems = getLineItemsApi(roofTopId, roNumber);
      setLineItemsObj(await lineItems);

      const mappedLineItems = (await lineItems).map(item => ({
        value: item.lineNumber,
        label: item.lineNumber,
      }));
      if (mappedLineItems.length > 0) {
        setLineNumbers(mappedLineItems);
        setShowLineNumberDropdown(true);
      } else {
        setShowLineNumberDropdown(false);
      }
    }
  };

  const checkIfRoExists = async (
    roofTopId: string,
    roNumber: string,
    setValue: UseFormSetValue<NewTicketFormValuesT>
  ): Promise<void> => {
    if (!roofTopId && !roNumber) {
      return;
    }
    if (roofTopId && roNumber && roNumber !== null) {
      setAdvisorLineNumber(roofTopId, roNumber, setValue);
    }
  };

  return {
    requestCategoriesOptions,
    isLoadingRequestCategoriesOptions:
      ticketRequestCategoriesResult.isLoading && !!effectiveRoofTopId,
    ticketRequestSubcategoriesOptions,
    isLoadingTicketRequestSubcategoriesOptions: ticketRequestSubcategoriesResult.isLoading,
    claimStatusesOptions,
    isLoadingClaimStatusesOptions: claimStatusesResult.isLoading,
    ticketPrioritiesOptions,
    isLoadingTicketPrioritiesOptions: ticketPrioritiesResult.isLoading,
    isLoadingRoofTopOptions: isLoadingUserProfile,
    roofTopIdsOptions,
    handleCloseNewTicketPopup,
    isPopupOpened: isNewTicketPopupOpened,
    handleSubmitForm,
    handleAmountBlur,
    isCreatingNewTicket: createTicketMutation.isLoading,
    handleRoofTopChange,
    handleCategoryChange,
    ticketRequestCategories: ticketRequestCategoriesResult.data,
    ticketRequestSubcategories: ticketRequestSubcategoriesResult.data,
    advisorNumber,
    checkIfRoExists,
    handleLineNumberChange,
    handleROChange,
    lineItemsObj,
    lineNumbers,
    recentRoData,
    setAdvisorNumber,
    setLineItemsObj,
    setLineNumbers,
    setRecentRoData,
    setSelectedLineNumber,
    setShowLineNumberDropdown,
    showLineNumberDropdown,
    setSelectedRoofTopId,
    handleAdvisorNumberChange,
  };
};

export default useOpenNewTicketPopup;
